import { AuthError } from '../auth/session.js';
import { deviceTimeZone } from '../timezone.js';

import type { BootstrapResponse, PullResponse, PushRequest, PushResponse } from '@nedelnik/shared';

/**
 * Транспорт синхронизации. Отделён от движка интерфейсом ради двух вещей:
 * тесты движка гоняются на подставном транспорте без сети, а порт на iOS
 * (раздел 11 архитектуры) меняет только этот слой.
 */

export interface Transport {
  push(request: PushRequest): Promise<PushResponse>;
  pull(params: { since: number; limit?: number }): Promise<PullResponse>;
  bootstrap(): Promise<BootstrapResponse>;
}

/** Ошибка с кодом от сервера: движок разводит поведение именно по нему. */
export class SyncHttpError extends Error {
  status: number;
  code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'SyncHttpError';
    this.status = status;
    this.code = code;
  }
}

/** Курсор ушёл ниже watermark: нужен bootstrap, а не повтор. */
export function isResyncRequired(error: unknown): boolean {
  return error instanceof SyncHttpError && error.status === 409 && error.code === 'resync_required';
}

/** Отказ по содержанию: повтор даст тот же ответ, поэтому мутация уходит в карантин. */
export function isPermanentRequestError(error: unknown): boolean {
  return (
    error instanceof SyncHttpError &&
    error.status >= 400 &&
    error.status < 500 &&
    error.status !== 401 &&
    error.status !== 408 &&
    error.status !== 409 &&
    error.status !== 429
  );
}

export function isAuthError(error: unknown): boolean {
  return error instanceof SyncHttpError && error.status === 401;
}

/**
 * Сервер вообще не ответил: обрыв, спящий телефон, выключенная сеть.
 *
 * Отличать это от ошибки сервера важно для индикатора: «нет связи» и «сервер
 * ответил ошибкой» — разные новости для человека. Опираться на
 * `navigator.onLine` для этого нельзя: он говорит лишь о том, что у машины есть
 * какое-то подключение, и остаётся `true`, когда до сервера всё равно не
 * достучаться.
 */
export function isUnreachable(error: unknown): boolean {
  return !(error instanceof SyncHttpError);
}

export interface HttpTransportOptions {
  /** Базовый адрес API. Пусто — тот же источник, что и страница. */
  baseUrl?: string;
  accessToken: () => Promise<string>;
  /** Обновить сессию по refresh-cookie. Вызывается один раз на запрос. */
  reauth?: () => Promise<void>;
  fetchImpl?: typeof fetch;
}

export function createHttpTransport(options: HttpTransportOptions): Transport {
  const base = (options.baseUrl ?? '').replace(/\/$/, '');
  const doFetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);

  /**
   * Токен для запроса — с переводом отказа сессии в наш HTTP-отказ.
   *
   * Без перевода «сессии больше нет» приезжало сюда как `AuthError`, то есть
   * как что угодно, только не `SyncHttpError`. А `isUnreachable` отвечает
   * ровно на вопрос «это не наш HTTP-отказ?» — и говорил «да». Движок считал
   * мёртвую сессию обрывом связи и показывал «оффлайн, N правок ждут» при
   * живом сервере. Бесконечно: чинится это входом, а не повтором, но экран
   * входа не появлялся, потому что никто не узнавал, что сессии нет.
   */
  async function accessToken(): Promise<string> {
    try {
      return await options.accessToken();
    } catch (error) {
      if (error instanceof AuthError) {
        throw new SyncHttpError(error.status, error.code, error.message);
      }
      throw error;
    }
  }

  async function request<T>(path: string, init: RequestInit, retryOn401 = true): Promise<T> {
    const token = await accessToken();

    // Пояс устройства — с каждым запросом: по нему сервер решает, когда для
    // этого человека наступил новый спринт (3.11.6).
    const timezone = deviceTimeZone();
    const response = await doFetch(`${base}${path}`, {
      ...init,
      headers: {
        ...(init.body === undefined ? {} : { 'content-type': 'application/json' }),
        authorization: `Bearer ${token}`,
        ...(timezone === undefined ? {} : { 'x-timezone': timezone }),
        ...init.headers,
      },
      credentials: 'include',
    });

    if (response.ok) {
      return (await response.json()) as T;
    }

    const body = (await response.json().catch(() => ({}))) as { error?: string; message?: string };
    const error = new SyncHttpError(
      response.status,
      body.error ?? 'http_error',
      body.message ?? `запрос ${path} вернул ${String(response.status)}`,
    );

    // Access живёт пятнадцать минут, и истечь он может ровно между двумя
    // запросами. Одна попытка обновиться — не «на всякий случай», а штатный ход.
    if (response.status === 401 && retryOn401 && options.reauth !== undefined) {
      await options.reauth();
      return request<T>(path, init, false);
    }

    throw error;
  }

  return {
    push: (payload) =>
      request<PushResponse>('/v1/sync/push', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    pull: (params) => {
      const query = new URLSearchParams({ since: String(params.since) });
      if (params.limit !== undefined) query.set('limit', String(params.limit));
      return request<PullResponse>(`/v1/sync/pull?${query.toString()}`, { method: 'GET' });
    },

    bootstrap: () => request<BootstrapResponse>('/v1/sync/bootstrap', { method: 'GET' }),
  };
}
